import {
  CTCSS_TONES,
  DTCS_CODES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const MEMORY_SIZE = 0x2000
const PROGRAMMABLE_SIZE = 0x1d00
const BLOCK_SIZE = 0x80
const CHANNEL_COUNT = 200
const CHANNEL_SIZE = 16
const ATTRIBUTE_START = 0x0d60
const NAME_START = 0x0f50
const NAME_SIZE = 16
const XOR_TABLE = [22, 108, 20, 230, 46, 145, 13, 64, 33, 53, 213, 64, 19, 3, 233, 128]
const APPROVED_PREFIXES = ['k5_2.01.', 'app_2.01.', '2.01.', '3.00.', '4.00.', 'k5_4.00.', '5.00.', '7.00.', '7.01.', '1o11', '1.02.']

type UVK5Spec = {
  id: string
  vendor: string
  model: string
  summary: string
  firmwarePrefixes: readonly string[]
}

export type UVK5Profile = {
  protocol: 'uvk5'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 38400
  channelMinimum: 1
  channelMaximum: number
  nameLength: 10
  memorySize: number
  firmwarePrefixes: readonly string[]
  powerLevels: readonly ['Low', 'Med', 'High']
}

const SPECS: readonly UVK5Spec[] = [
  {
    id: 'quansheng-uv-k5',
    vendor: 'Quansheng',
    model: 'UV-K5',
    summary: 'Quansheng UV-K5 multi-band handheld.',
    firmwarePrefixes: APPROVED_PREFIXES,
  },
  {
    id: 'retevis-ra79',
    vendor: 'Retevis',
    model: 'RA79',
    summary: 'Retevis RA79 in the Quansheng UV-K5 family.',
    firmwarePrefixes: APPROVED_PREFIXES,
  },
  {
    id: 'maxtalker-tk-6',
    vendor: 'MaxTalker',
    model: 'TK-6',
    summary: 'MaxTalker TK-6 in the Quansheng UV-K5 family.',
    firmwarePrefixes: APPROVED_PREFIXES,
  },
  {
    id: 'quansheng-uv-k1',
    vendor: 'Quansheng',
    model: 'UV-K1',
    summary: 'Quansheng UV-K1 with UV-K5 channel memory.',
    firmwarePrefixes: ['7.03.'],
  },
]

export const UVK5_PROFILES: readonly UVK5Profile[] = SPECS.map((spec) => ({
  protocol: 'uvk5',
  id: spec.id,
  vendor: spec.vendor,
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '200 channels', '10-character names', 'Firmware gated'],
  baudRate: 38400,
  channelMinimum: 1,
  channelMaximum: CHANNEL_COUNT,
  nameLength: 10,
  memorySize: MEMORY_SIZE,
  firmwarePrefixes: spec.firmwarePrefixes,
  powerLevels: ['Low', 'Med', 'High'],
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'Low',
    skip: false,
  }
}

function readUint32LE(raw: Uint8Array, offset: number): number {
  return (
    raw[offset] |
    (raw[offset + 1] << 8) |
    (raw[offset + 2] << 16) |
    (raw[offset + 3] << 24)
  ) >>> 0
}

function writeUint32LE(raw: Uint8Array, offset: number, value: number): void {
  raw[offset] = value & 0xff
  raw[offset + 1] = (value >>> 8) & 0xff
  raw[offset + 2] = (value >>> 16) & 0xff
  raw[offset + 3] = (value >>> 24) & 0xff
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(type: number, index: number): DecodedTone {
  if (type === 1 && index < CTCSS_TONES.length) {
    return {
      mode: 'ctcss',
      ctcss: CTCSS_TONES[index],
      dtcs: null,
      polarity: 'N',
    }
  }
  if ((type === 2 || type === 3) && index < DTCS_CODES.length) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: DTCS_CODES[index],
      polarity: type === 3 ? 'R' : 'N',
    }
  }
  return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
}

function decodeName(image: Uint8Array, number: number): string {
  const start = NAME_START + (number - 1) * NAME_SIZE
  return String.fromCharCode(
    ...image.slice(start, start + 10),
  ).replace(/[\u0000\ufffd]/g, '').trim()
}

export function parseUVK5Channel(
  image: Uint8Array,
  profile: UVK5Profile,
  number: number,
): WebChannel {
  const start = (number - 1) * CHANNEL_SIZE
  const raw = image.slice(start, start + CHANNEL_SIZE)
  const attribute = image[ATTRIBUTE_START + number - 1]
  if (
    raw.byteLength !== CHANNEL_SIZE ||
    (attribute & 0x08) !== 0 ||
    readUint32LE(raw, 0) === 0 ||
    readUint32LE(raw, 0) === 0xffffffff
  ) {
    return emptyChannel(number)
  }
  const rxFrequency = readUint32LE(raw, 0) * 10
  const offset = readUint32LE(raw, 4) * 10
  const shift = raw[11] & 0x03
  let txFrequency =
    shift === 1 ? rxFrequency + offset : shift === 2 ? rxFrequency - offset : rxFrequency
  if (raw[12] & 0x01) {
    txFrequency = rxFrequency
  }
  const txTone = decodeTone(raw[10] >> 4, raw[9])
  const rxTone = decodeTone(raw[10] & 0x0f, raw[8])
  const selected = txTone.mode === 'none' ? rxTone : txTone
  const mode =
    raw[11] & 0x10
      ? raw[12] & 0x02
        ? 'NAM'
        : 'AM'
      : raw[12] & 0x02
        ? 'NFM'
        : 'FM'
  return {
    number,
    empty: false,
    name: decodeName(image, number),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode,
    power: profile.powerLevels[(raw[12] >> 2) & 0x03] ?? 'Low',
    skip: false,
  }
}

export function parseUVK5Channels(
  image: Uint8Array,
  profile: UVK5Profile,
): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, index) =>
    parseUVK5Channel(image, profile, index + 1),
  )
}

function setTone(raw: Uint8Array, channel: WebChannel): void {
  let type = 0
  let index = 0
  if (channel.toneMode === 'ctcss') {
    type = 1
    index = CTCSS_TONES.indexOf(
      channel.ctcss as (typeof CTCSS_TONES)[number],
    )
    if (index < 0) throw new Error('That CTCSS tone is not supported by the UV-K5.')
  } else if (channel.toneMode === 'dtcs') {
    type = channel.dtcsPolarity === 'R' ? 3 : 2
    index = DTCS_CODES.indexOf(
      channel.dtcs as (typeof DTCS_CODES)[number],
    )
    if (index < 0) throw new Error('That DCS code is not supported by the UV-K5.')
  }
  raw[8] = index
  raw[9] = index
  raw[10] = (type << 4) | type
}

export function applyUVK5Channel(
  image: Uint8Array,
  profile: UVK5Profile,
  channel: WebChannel,
): void {
  const start = (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(start, start + CHANNEL_SIZE)
  const attribute = ATTRIBUTE_START + channel.number - 1
  if (channel.empty) {
    raw.fill(0xff)
    image[attribute] |= 0x08
    image.fill(0, NAME_START + (channel.number - 1) * NAME_SIZE, NAME_START + channel.number * NAME_SIZE)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (raw.every((value) => value === 0xff)) raw.fill(0)
  writeUint32LE(raw, 0, Math.round(channel.rxFrequency / 10))
  const tx = channel.txFrequency ?? channel.rxFrequency
  const offset = Math.abs(tx - channel.rxFrequency)
  writeUint32LE(raw, 4, Math.round(offset / 10))
  raw[11] =
    (raw[11] & 0xfc) |
    (tx > channel.rxFrequency ? 1 : tx < channel.rxFrequency ? 2 : 0)
  raw[11] =
    (raw[11] & 0xef) |
    (channel.mode === 'AM' || channel.mode === 'NAM' ? 0x10 : 0)
  raw[12] =
    (raw[12] & 0xe1) |
    (channel.mode === 'NFM' || channel.mode === 'NAM' ? 0x02 : 0) |
    ((
      Math.max(
        0,
        profile.powerLevels.indexOf(
          channel.power as (typeof profile.powerLevels)[number],
        ),
      ) & 0x03
    ) << 2)
  setTone(raw, channel)
  raw[14] = 0
  raw[15] &= 0xf0
  image[attribute] &= ~0x08
  image.fill(
    0,
    NAME_START + (channel.number - 1) * NAME_SIZE,
    NAME_START + channel.number * NAME_SIZE,
  )
  const name = channel.name
    .replace(/[^\x20-\x7e]/g, '')
    .slice(0, 10)
  for (let index = 0; index < name.length; index += 1) {
    image[NAME_START + (channel.number - 1) * NAME_SIZE + index] =
      name.charCodeAt(index)
  }
}

function xorBytes(data: Uint8Array): Uint8Array {
  return data.map((value, index) => value ^ XOR_TABLE[index % XOR_TABLE.length])
}

function crc16Xmodem(data: Uint8Array): number {
  let crc = 0
  for (const byte of data) {
    crc ^= byte << 8
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff
    }
  }
  return crc
}

async function sendCommand(
  transport: RadioTransport,
  data: Uint8Array,
): Promise<void> {
  const protectedData = new Uint8Array(data.length + 2)
  protectedData.set(data)
  const crc = crc16Xmodem(data)
  protectedData[data.length] = crc & 0xff
  protectedData[data.length + 1] = (crc >>> 8) & 0xff
  const packet = new Uint8Array(8 + protectedData.length)
  packet.set([0xab, 0xcd, data.length & 0xff, 0x00])
  packet.set(xorBytes(protectedData), 4)
  const footer = packet.length - 2
  packet[footer] = 0xdc
  packet[footer + 1] = 0xba
  await transport.write(packet)
}

async function receiveReply(transport: RadioTransport): Promise<Uint8Array> {
  const header = await transport.readExact(4, 2500)
  if (header[0] !== 0xab || header[1] !== 0xcd || header[3] !== 0) {
    throw new Error('Unexpected UV-K5 response header.')
  }
  const length = header[2]
  const encoded = await transport.readExact(length, 3500)
  const footer = await transport.readExact(4, 2500)
  if (footer[2] !== 0xdc || footer[3] !== 0xba) {
    throw new Error('Unexpected UV-K5 response footer.')
  }
  return xorBytes(encoded)
}

async function sayHello(profile: UVK5Profile, transport: RadioTransport): Promise<string> {
  await transport.discardInput()
  for (let attempt = 0; attempt < 5; attempt += 1) {
    await sendCommand(transport, Uint8Array.of(0x14, 0x05, 0x04, 0x00, 0x6a, 0x39, 0x57, 0x64))
    const reply = await receiveReply(transport)
    if (reply.length >= 5 && reply[0] === 0x18 && reply[1] === 0x05) {
      throw new Error('The UV-K5 is already in programming mode; restart it normally.')
    }
    let end = Math.min(28, reply.length)
    while (end > 4 && (reply[end - 1] < 0x20 || reply[end - 1] > 0x7e)) end -= 1
    const firmware = new TextDecoder().decode(reply.slice(4, end))
    if (firmware) {
      if (!profile.firmwarePrefixes.some((prefix) => firmware.startsWith(prefix))) {
        throw new Error(`Firmware ${firmware} is not approved for browser writes.`)
      }
      return firmware
    }
  }
  throw new Error('The UV-K5 did not provide a firmware identity.')
}

async function readMemory(
  transport: RadioTransport,
  offset: number,
  length: number,
): Promise<Uint8Array> {
  const command = new Uint8Array(12)
  command.set([0x1b, 0x05, 0x08, 0x00])
  command[4] = offset & 0xff
  command[5] = (offset >>> 8) & 0xff
  command[6] = length
  command[7] = 0
  command.set([0x6a, 0x39, 0x57, 0x64], 8)
  await sendCommand(transport, command)
  return (await receiveReply(transport)).slice(8)
}

async function writeMemory(
  transport: RadioTransport,
  data: Uint8Array,
  offset: number,
): Promise<void> {
  const command = new Uint8Array(16 + data.length)
  command.set([0x1d, 0x05, data.length + 8, 0, offset & 0xff, (offset >>> 8) & 0xff, data.length, 1])
  command.set([0x6a, 0x39, 0x57, 0x64], 8)
  command.set(data, 12)
  await sendCommand(transport, command)
  const reply = await receiveReply(transport)
  if (
    reply[0] !== 0x1e ||
    reply[4] !== (offset & 0xff) ||
    reply[5] !== ((offset >>> 8) & 0xff)
  ) {
    throw new Error(`The UV-K5 rejected write block 0x${offset.toString(16)}.`)
  }
}

async function resetRadio(transport: RadioTransport): Promise<void> {
  await sendCommand(transport, Uint8Array.of(0xdd, 0x05, 0x00, 0x00))
}

export async function readUVK5RadioWithTransport(
  profile: UVK5Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const firmware = await sayHello(profile, transport)
  const image = new Uint8Array(MEMORY_SIZE)
  const total = MEMORY_SIZE / BLOCK_SIZE
  for (let index = 0; index < total; index += 1) {
    const address = index * BLOCK_SIZE
    const data = await readMemory(transport, address, BLOCK_SIZE)
    if (data.length !== BLOCK_SIZE) {
      throw new Error(`UV-K5 read block 0x${address.toString(16)} was incomplete.`)
    }
    image.set(data, address)
    onProgress?.({
      current: index + 1,
      total,
      label: 'Reading Quansheng UV-K5 memory',
    })
  }
  return { image, reportedModel: `${profile.model} (${firmware})` }
}

export async function writeUVK5RadioWithTransport(
  profile: UVK5Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than UV-K5 memory.')
  }
  await sayHello(profile, transport)
  const total = PROGRAMMABLE_SIZE / BLOCK_SIZE
  try {
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      await writeMemory(
        transport,
        image.slice(address, address + BLOCK_SIZE),
        address,
      )
      onProgress?.({
        current: index + 1,
        total,
        label: 'Writing Quansheng UV-K5 memory',
      })
    }
  } finally {
    await resetRadio(transport)
  }
}
